import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Eye, Download, Loader2, FileText, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function ReportPreviewDialog({ caseId, caseStatus, trigger, className }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [fileName, setFileName] = useState('');

  const isFinal = caseStatus === 'completed' || caseStatus === 'verified' || caseStatus === 'report_approved';

  const handlePreview = useCallback(async (forceRefresh = false) => {
    setOpen(true);
    setLoading(true);
    setPdfUrl(null);
    try {
      const response = await axios.post(`/cases/${caseId}/generate-bgv-report`, {
        is_final: isFinal,
        force_refresh: !!forceRefresh,
      }, { responseType: 'blob' });

      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?(.+)"?/);
      const name = match ? match[1] : `BGV_Report_${caseId}_${new Date().toISOString().slice(0, 10)}.pdf`;
      // Wrap as a File (preserves filename metadata on save where supported)
      // and append a #filename= URL fragment as a hint to PDF viewers.
      // NOTE: Chrome's built-in PDF viewer still saves blob URLs using
      // their UUID basename — there is no reliable cross-browser way to
      // force a custom filename inside an inline PDF viewer. The
      // explicit "Download PDF" button below uses link.download to
      // guarantee the correct filename.
      const file = new File([response.data], name, { type: 'application/pdf' });
      const baseUrl = window.URL.createObjectURL(file);
      const url = `${baseUrl}#filename=${encodeURIComponent(name)}`;
      setPdfUrl(url);
      setPdfBlob(file);
      setFileName(name);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [caseId, isFinal]);

  const handleDownload = () => {
    if (!pdfBlob) return;
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  const handleClose = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfBlob(null);
    setOpen(false);
  };

  return (
    <>
      {trigger ? (
        <span onClick={handlePreview} className={`cursor-pointer ${className || ''}`}>{trigger}</span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreview}
          className={className}
          data-testid={`preview-report-btn-${caseId}`}
        >
          <Eye className="w-4 h-4 mr-1.5" />
          Preview Report
        </Button>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-[90vw] w-[90vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-4 py-3 border-b bg-slate-50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-base">
                <FileText className="w-4 h-4" />
                {isFinal ? 'Final' : 'Interim'} Report Preview — {caseId}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {pdfUrl && isFinal && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePreview(true)}
                    data-testid="regenerate-report-btn"
                    title="Bypass cache and regenerate from current data"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    Regenerate
                  </Button>
                )}
                {pdfUrl && (
                  <Button size="sm" onClick={handleDownload} data-testid="download-report-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Download className="w-4 h-4 mr-1.5" />
                    Download PDF
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 bg-slate-200">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
                  <p className="text-sm text-slate-600">Generating report...</p>
                </div>
              </div>
            )}
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0"
                title="Report Preview"
              />
            )}
            {!loading && !pdfUrl && (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-slate-500">No report generated</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
